import React, {
  MouseEvent,
  useRef,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import {
  useNavigate,
} from 'react-router-dom';
import isNil from 'lodash/isNil';
import {
  ToDo,
} from 'types/toDo';
import {
  useMedia,
} from 'core/hooks';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import Name from 'pages/Dashboard/components/Name';
import ToolTip from 'core/components/Tooltip';
import Urgent from 'pages/Dashboard/pages/Todos/components/Urgent';
import AbandonButton from 'pages/Dashboard/pages/Todos/components/AbandonButton';
import ApplicableStatusSelect from 'pages/Dashboard/pages/Todos/components/ApplicableStatusSelect';
import UrgentIcon from 'core/icons/urgent';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import EditableCollaboratorsSummary from 'pages/Dashboard/pages/Todos/components/EditableCollaboratorsSummary';
import EditableAssignee from 'pages/Dashboard/pages/Todos/components/EditableAssignee';
import {
  useUpdateToDoHandler,
} from 'pages/Dashboard/pages/Todos/hooks';
import EditableDueDate from 'pages/Dashboard/pages/Todos/components/EditableDueDate';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';

type PatientCellProps = {
  row: Row<ToDo>;
};

function DueDateCell({ row }: PatientCellProps) {
  return (
    <EditableDueDate
      toDo={row.original}
      className="flex items-center !py-1"
      classes={{
        value: '!text-xs !font-medium !py-1',
        emptyValue: '!text-xs !font-medium !py-1',
        popover: 'max-w-[120px]',
      }}
      displayVariant="text"
      hideEditIcon={Boolean(row.original.dueDate)}
    />
  );
}

function PatientCell({ row }: PatientCellProps) {
  const { getMediaPathById } = useMedia();
  const navigate = useNavigate();
  const link = getPatientChartPath(row.original.patient?.patientId);

  return (
    <Name
      link={{
        to: link,
        onClick: (event: MouseEvent<HTMLAnchorElement>) => {
          event.stopPropagation();
          if (!isNil(link)) {
            navigate(link);
          }
        },
      }}
      name={getDisplayName(row.original.patient?.formattedNames)}
      image={{ src: getMediaPathById(row.original.patientId) ?? undefined }}
    />
  );
}

function AssignedToSummary({ row }: PatientCellProps) {
  const assignedUsers = row.original.assignedUsers ?? [];
  const [primaryAssignee, ...remainingAssignees] = assignedUsers;
  const primaryAssigneeSrc = useUserProfilePicturePath(primaryAssignee?.userId);

  return assignedUsers.length === 0 ? <span className="text-gray-400">--</span> : (
    <ToolTip
      title={(
        <div className="flex flex-col gap-1">
          {assignedUsers.map((user) => (
            <span key={user?.userId}>
              {getDisplayName(user?.formattedNames, '--')}
            </span>
          ))}
        </div>
      )}
      isInteractive
      hide={assignedUsers.length <= 1}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Name
          {...(primaryAssignee?.formattedNames ?? {})}
          image={{
            src: primaryAssigneeSrc,
          }}
          classes={{
            root: '!min-w-0',
            label: 'truncate',
          }}
        />
        {remainingAssignees.length > 0 && (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
            +{remainingAssignees.length}
          </span>
        )}
      </div>
    </ToolTip>
  );
}

function AssignedToCell({ row }: PatientCellProps) {
  const {
    asyncHandler: updateToDo,
    isLoading: isUpdating,
  } = useUpdateToDoHandler({});
  const toDo = row.original;

  return (
    <EditableAssignee
      toDo={toDo}
      isUpdating={isUpdating}
      idleView={<AssignedToSummary row={row} />}
      className="w-full mr-2"
      controlClassName="!w-full !min-w-0"
      popperClassName="!max-w-[calc(100vw-2rem)]"
      showEditIconOnHover
      handleChange={(update) => {
        updateToDo({
          toDoId: toDo.toDoId,
          dueDate: toDo.dueDate ?? null,
          ...update,
        });
      }}
    />
  );
}

function TitleCell({ row: { original } }: PatientCellProps) {
  const titleRef = useRef<HTMLSpanElement>(null);

  return (
    <div className="flex min-w-0 flex-col gap-1.5 py-0.5">
      <div className="flex min-w-0 items-start gap-2">
        <ToolTip title={original.name} auto ref={titleRef}>
          <span
            ref={titleRef}
            className="min-w-0 truncate !text-xs font-semibold text-gray-800">
              {original.name}
          </span>
        </ToolTip>
      </div>
      <span className="inline-flex w-fit rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 !text-xs font-medium text-gray-500">
        {original.category || '--'}
      </span>
    </div>
  );
}

function ContributorsCell({ row }: PatientCellProps) {
  return (
    <div
      role="button"
      tabIndex={-1}
      className="flex items-center"
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
    >
      <EditableCollaboratorsSummary
        toDo={row.original}
        className="-ml-1"
      />
    </div>
  );
}

function CreatedCell({ row }: PatientCellProps) {
  const creatorDisplayName = getDisplayName(row.original.creator?.formattedNames, '--');
  const creatorSrc = useUserProfilePicturePath(row.original.creator?.userId);

  return (
    <Name
      {...row.original.creator?.formattedNames}
      name={creatorDisplayName}
      image={{
        size: 'sm',
        src: creatorSrc,
      }}
      classes={{
        root: '!min-w-0 !gap-2 !font-normal',
        nameRoot: '!min-w-0',
        label: '!text-xs !font-medium !leading-4 !text-gray-900',
      }}
    >
      <span className="truncate text-xs font-normal leading-4 text-gray-500">
        {row.original.formattedDate || '--'}
      </span>
    </Name>
  );
}

const columns: ColumnDef<ToDo>[] = [{
  id: 'isUrgent',
  columnTitle: (
    <div className="w-full text-center">
      <UrgentIcon className="-ml-2 mt-1 !w-5 !h-5 !text-gray-700" />
    </div>
  ),
  size: 48,
  minSize: 48,
  cell: ({ row }) => (<Urgent {...row.original} className="top-1 left-1" />),
}, {
  id: 'status',
  header: 'Status',
  size: 130,
  minSize: 110,
  cell: ({ row }) => (
    <ApplicableStatusSelect
      className="!w-full"
      toDoId={row.original.toDoId}
      dueDate={row.original.dueDate ?? null}
      value={row.original.currentStatus}
      isAbandoned={row.original.isAbandoned}
      options={row.original.availableStatuses ?? []}
    />
  ),
}, {
  id: 'patient',
  header: 'Patient',
  size: 190,
  minSize: 150,
  maxSize: 240,
  cell: ({ row }) => <PatientCell row={row} />,
}, {
  id: 'name',
  header: 'Title',
  meta: { autoSized: true },
  minSize: 180,
  cell: ({ row }) => <TitleCell row={row} />,
}, {
  id: 'dueDate',
  accessorKey: 'dueDate',
  header: 'Due date',
  size: 150,
  minSize: 110,
  className: '!overflow-visible',
  cell: ({ row }) => <DueDateCell row={row} />,
}, {
  id: 'assignee',
  header: 'Assigned to',
  size: 220,
  minSize: 180,
  className: '!overflow-visible',
  cell: ({ row }) => <AssignedToCell row={row} />,
}, {
  id: 'collaborator',
  header: 'Collaborator',
  size: 120,
  minSize: 100,
  cell: ({ row }) => <ContributorsCell row={row} />,
}, {
  id: 'created',
  accessorKey: 'createdOn',
  header: 'Created',
  size: 170,
  minSize: 150,
  cell: ({ row }) => <CreatedCell row={row} />,
}, {
  id: 'actions',
  size: 48,
  minSize: 48,
  cell: ({ row }) => (
    <AbandonButton toDo={row.original} />
  ),
}];

export default columns;
